import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  TrendingUp,
  ShowChart,
  Send,
  CheckCircle,
} from '@mui/icons-material';
import QuoteCard from './QuoteCard';
import { useQuoteStream } from '../../hooks/useQuoteStream';

interface OrderTypes {
  orderTypes: Array<{ value: string; label: string }>;
  actions: Array<{ value: string; label: string }>;
  timeInForce: Array<{ value: string; label: string }>;
  secTypes: Array<{ value: string; label: string }>;
}

interface TradeFormData {
  symbol: string;
  secType: string;
  exchange: string;
  quantity: number;
  orderType: string;
  limitPrice?: number | undefined;
  action: string;
  timeInForce: string;
}

// Common exchanges
const EXCHANGES = [
  { value: 'NYSE', label: 'NYSE' },
  { value: 'NASDAQ', label: 'NASDAQ' },
  { value: 'AMEX', label: 'AMEX' },
  { value: 'ARCA', label: 'ARCA' },
  { value: 'BATS', label: 'BATS' },
  { value: 'IEX', label: 'IEX' },
];

// Auto-detection lookup for well-known symbols
const SYMBOL_EXCHANGE_MAP: { [key: string]: string } = {
  // NASDAQ (Tech-heavy)
  'AAPL': 'NASDAQ',
  'MSFT': 'NASDAQ',
  'GOOGL': 'NASDAQ',
  'GOOG': 'NASDAQ',
  'AMZN': 'NASDAQ',
  'TSLA': 'NASDAQ',
  'META': 'NASDAQ',
  'NVDA': 'NASDAQ',
  'NFLX': 'NASDAQ',
  'AMD': 'NASDAQ',
  'INTC': 'NASDAQ',
  'CSCO': 'NASDAQ',
  'ADBE': 'NASDAQ',
  'PYPL': 'NASDAQ',
  'CMCSA': 'NASDAQ',
  'PEP': 'NASDAQ',
  'COST': 'NASDAQ',
  'AVGO': 'NASDAQ',
  'QCOM': 'NASDAQ',
  'TXN': 'NASDAQ',
  'AMGN': 'NASDAQ',
  'ISRG': 'NASDAQ',
  'BKNG': 'NASDAQ',
  'VRTX': 'NASDAQ',
  'FISV': 'NASDAQ',
  'ADI': 'NASDAQ',
  'REGN': 'NASDAQ',
  'CDNS': 'NASDAQ',
  'ALGN': 'NASDAQ',
  'SNPS': 'NASDAQ',
  'KLAC': 'NASDAQ',
  'CTSH': 'NASDAQ',
  'MCHP': 'NASDAQ',
  'IDXX': 'NASDAQ',
  'PAYX': 'NASDAQ',
  'BIIB': 'NASDAQ',
  'DXCM': 'NASDAQ',
  'FAST': 'NASDAQ',
  'CTAS': 'NASDAQ',
  'WDAY': 'NASDAQ',
  'PCAR': 'NASDAQ',
  'ANSS': 'NASDAQ',
  'TEAM': 'NASDAQ',
  'SWKS': 'NASDAQ',
  'NTES': 'NASDAQ',
  'VRSN': 'NASDAQ',
  'CHTR': 'NASDAQ',
  'LRCX': 'NASDAQ',
  'MXIM': 'NASDAQ',
  'SPLK': 'NASDAQ',
  'CDW': 'NASDAQ',
  'FTNT': 'NASDAQ',
  'CPRT': 'NASDAQ',
  'EXPE': 'NASDAQ',
  'XEL': 'NASDAQ',
  'ZM': 'NASDAQ',
  'DOCU': 'NASDAQ',
  'ROKU': 'NASDAQ',
  'PTON': 'NASDAQ',
  'UBER': 'NASDAQ',
  'LYFT': 'NASDAQ',
  'SQ': 'NASDAQ',
  'SHOP': 'NASDAQ',
  'CRWD': 'NASDAQ',
  'OKTA': 'NASDAQ',
  'ZS': 'NASDAQ',
  'NET': 'NASDAQ',
  'DDOG': 'NASDAQ',
  'SNOW': 'NASDAQ',
  'PLTR': 'NASDAQ',
  'COIN': 'NASDAQ',
  'HOOD': 'NASDAQ',
  'RIVN': 'NASDAQ',
  'LCID': 'NASDAQ',
  // NYSE (Many large companies)
  'JPM': 'NYSE',
  'BAC': 'NYSE',
  'WMT': 'NYSE',
  'JNJ': 'NYSE',
  'V': 'NYSE',
  'PG': 'NYSE',
  'UNH': 'NYSE',
  'HD': 'NYSE',
  'MA': 'NYSE',
  'DIS': 'NYSE',
  'VZ': 'NYSE',
  'CVX': 'NYSE',
  'XOM': 'NYSE',
  'ABBV': 'NYSE',
  'ABT': 'NYSE',
  'T': 'NYSE',
  'KO': 'NYSE',
  'MRK': 'NYSE',
  'NKE': 'NYSE',
  'MCD': 'NYSE',
  'PM': 'NYSE',
  'TMO': 'NYSE',
  'LIN': 'NYSE',
  'BMY': 'NYSE',
  'ACN': 'NYSE',
  'HON': 'NYSE',
  'UPS': 'NYSE',
  'RTX': 'NYSE',
  'LMT': 'NYSE',
  'NOC': 'NYSE',
  'BA': 'NYSE',
  'GE': 'NYSE',
  'CAT': 'NYSE',
  'DE': 'NYSE',
  'MMM': 'NYSE',
  'EMR': 'NYSE',
  'ETN': 'NYSE',
  'ITW': 'NYSE',
  'PH': 'NYSE',
  'ROK': 'NYSE',
  'AME': 'NYSE',
  'GGG': 'NYSE',
  'DOV': 'NYSE',
  'CMI': 'NYSE',
  'FTV': 'NYSE',
  'WWD': 'NYSE',
  'IEX': 'NYSE',
  'ZBH': 'NYSE',
  'RBC': 'NYSE',
  'PNR': 'NYSE',
  'TTC': 'NYSE',
  'FLS': 'NYSE',
  'GDI': 'NYSE',
  'VMI': 'NYSE',
  'FLOW': 'NYSE',
  'AOS': 'NYSE',
  'SWK': 'NYSE',
};

// Function to auto-detect exchange based on symbol
const detectExchange = (symbol: string): string => {
  const upperSymbol = symbol.trim().toUpperCase();
  
  // Check lookup table first
  if (SYMBOL_EXCHANGE_MAP[upperSymbol]) {
    return SYMBOL_EXCHANGE_MAP[upperSymbol];
  }
  
  // Default to NYSE if not found
  return 'NYSE';
};

const Trade: React.FC = () => {
  const [orderTypes, setOrderTypes] = useState<OrderTypes | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [formData, setFormData] = useState<TradeFormData>({
    symbol: '',
    secType: 'STK',
    exchange: 'NYSE', // Default to NYSE
    quantity: 1,
    orderType: 'MARKET',
    limitPrice: undefined,
    action: 'BUY_TO_OPEN', // Server will provide available actions via order-types endpoint
    timeInForce: 'DAY',
  });

  // Debounce the symbol field so we don't subscribe to a new ws stream
  // on every keystroke. The hook handles ws subscribe/unsubscribe.
  const [debouncedSymbol, setDebouncedSymbol] = useState<string | null>(null);
  useEffect(() => {
    const trimmed = formData.symbol.trim().toUpperCase();
    if (!trimmed) {
      setDebouncedSymbol(null);
      return;
    }
    const t = setTimeout(() => setDebouncedSymbol(trimmed), 350);
    return () => clearTimeout(t);
  }, [formData.symbol]);

  const { quote, status: quoteStatus } = useQuoteStream(debouncedSymbol);
  const livePrice = useMemo(
    () => quote?.price ?? quote?.last ?? null,
    [quote],
  );

  useEffect(() => {
    fetchOrderTypes();
  }, []);

  const fetchOrderTypes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/trade/order-types`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrderTypes(data);
      }
    } catch (err) {
      console.error('Failed to fetch order types:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof TradeFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear limit price if switching to market order
    if (field === 'orderType' && value === 'MARKET') {
      setFormData(prev => ({ ...prev, limitPrice: undefined }));
    }
    
    // Auto-detect exchange when symbol changes
    if (field === 'symbol' && value && value.trim()) {
      const detectedExchange = detectExchange(value);
      setFormData(prev => ({
        ...prev,
        symbol: value,
        exchange: detectedExchange,
      }));
    }
  };

  const validateForm = (): string | null => {
    if (!formData.symbol.trim()) return 'Symbol is required';
    if (formData.quantity <= 0) return 'Quantity must be greater than 0';
    if (!formData.exchange || !formData.exchange.trim()) return 'Exchange is required';
    if (formData.orderType === 'LIMIT' && (!formData.limitPrice || formData.limitPrice <= 0)) {
      return 'Limit price is required for limit orders';
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Prepare payload - remove empty exchange, only include limitPrice for limit orders
      const payload: any = {
        symbol: formData.symbol.trim().toUpperCase(),
        secType: formData.secType,
        quantity: formData.quantity,
        orderType: formData.orderType,
        action: formData.action,
        timeInForce: formData.timeInForce,
      };

      // Only include limitPrice for limit orders
      if (formData.orderType === 'LIMIT' && formData.limitPrice && formData.limitPrice > 0) {
        payload.limitPrice = formData.limitPrice;
      }

      // Include exchange (server requires it)
      payload.exchange = formData.exchange.trim();

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/trade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(`Order placed successfully! Order ID: ${result.orderId}`);
        setFormData({
          symbol: '',
          secType: 'STK',
          exchange: 'NYSE', // Reset to default
          quantity: 1,
          orderType: 'MARKET',
          limitPrice: undefined,
          action: 'BUY_TO_OPEN', // Server will provide available actions via order-types endpoint
          timeInForce: 'DAY',
        });
      } else {
        setError(result.message || 'Failed to place order');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Trade submission error:', err);
    } finally {
      setSubmitting(false);
      setConfirmDialog(false);
    }
  };

  const getActionColor = (action: string) => {
    return action.includes('BUY') ? 'success' : 'error';
  };

  const getOrderTypeColor = (orderType: string) => {
    return orderType === 'MARKET' ? 'primary' : 'secondary';
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
        Trading Platform
      </Typography>

      <Grid container spacing={3}>
        {/* Trading Form */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <ShowChart sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Place Stock Order
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Symbol"
                    value={formData.symbol}
                    onChange={(e) => handleInputChange('symbol', e.target.value.toUpperCase())}
                    placeholder="e.g., AAPL, TSLA, MSFT"
                    variant="outlined"
                    helperText={formData.symbol ? `Exchange auto-detected: ${formData.exchange}` : 'Enter symbol to auto-detect exchange'}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Security Type</InputLabel>
                    <Select
                      value={formData.secType}
                      onChange={(e) => handleInputChange('secType', e.target.value)}
                      label="Security Type"
                    >
                      {orderTypes?.secTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Exchange</InputLabel>
                    <Select
                      value={formData.exchange}
                      onChange={(e) => handleInputChange('exchange', e.target.value)}
                      label="Exchange"
                    >
                      {EXCHANGES.map((exchange) => (
                        <MenuItem key={exchange.value} value={exchange.value}>
                          {exchange.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => handleInputChange('quantity', parseInt(e.target.value) || 0)}
                    variant="outlined"
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Order Type</InputLabel>
                    <Select
                      value={formData.orderType}
                      onChange={(e) => handleInputChange('orderType', e.target.value)}
                      label="Order Type"
                    >
                      {orderTypes?.orderTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {formData.orderType === 'LIMIT' && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Limit Price"
                      type="number"
                      value={formData.limitPrice ?? ''}
                      onChange={(e) => handleInputChange('limitPrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                      variant="outlined"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Action</InputLabel>
                    <Select
                      value={formData.action}
                      onChange={(e) => handleInputChange('action', e.target.value)}
                      label="Action"
                    >
                      {orderTypes?.actions.map((action) => (
                        <MenuItem key={action.value} value={action.value}>
                          {action.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Time in Force</InputLabel>
                    <Select
                      value={formData.timeInForce}
                      onChange={(e) => handleInputChange('timeInForce', e.target.value)}
                      label="Time in Force"
                    >
                      {orderTypes?.timeInForce.map((tif) => (
                        <MenuItem key={tif.value} value={tif.value}>
                          {tif.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<TrendingUp />}
                  label={formData.action}
                  color={getActionColor(formData.action)}
                  variant="outlined"
                />
                <Chip
                  label={formData.orderType}
                  color={getOrderTypeColor(formData.orderType)}
                  variant="outlined"
                />
                <Chip
                  label={`${formData.quantity} shares`}
                  color="default"
                  variant="outlined"
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => setConfirmDialog(true)}
                  disabled={submitting || !formData.symbol.trim() || !formData.exchange.trim()}
                  startIcon={submitting ? <CircularProgress size={20} /> : <Send />}
                  sx={{ py: 1.5 }}
                >
                  {submitting ? 'Placing Order...' : 'Place Order'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Live quote + order summary */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <QuoteCard
                symbol={debouncedSymbol}
                quote={quote}
                status={quoteStatus}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Order Summary
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Symbol:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formData.symbol || 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Action:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formData.action}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Quantity:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formData.quantity} shares
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Order Type:</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formData.orderType}
                    </Typography>
                  </Box>
                  {formData.orderType === 'LIMIT' && formData.limitPrice && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">Limit Price:</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        ${formData.limitPrice.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">Est. Total:</Typography>
                    <Typography variant="h6" fontWeight={600} color="primary">
                      {formData.orderType === 'MARKET'
                        ? livePrice !== null
                          ? `≈ $${(livePrice * formData.quantity).toFixed(2)}`
                          : `Market × ${formData.quantity}`
                        : formData.limitPrice
                          ? `$${(formData.limitPrice * formData.quantity).toFixed(2)}`
                          : 'N/A'
                      }
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Order</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please confirm your order details:
          </Typography>
          <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>{formData.action}</strong> {formData.quantity} shares of <strong>{formData.symbol}</strong>
            </Typography>
            <Typography variant="body2">
              Order Type: <strong>{formData.orderType}</strong>
            </Typography>
            {formData.orderType === 'LIMIT' && formData.limitPrice && (
              <Typography variant="body2">
                Limit Price: <strong>${formData.limitPrice.toFixed(2)}</strong>
              </Typography>
            )}
            <Typography variant="body2">
              Time in Force: <strong>{formData.timeInForce}</strong>
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This order will be executed through Alpaca. Please ensure you have sufficient funds.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <CheckCircle />}
          >
            {submitting ? 'Placing...' : 'Confirm Order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Trade;